import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiService } from "../../api/apiService";
import OknoPelnegoKomunikatu from "./OknoPelnegoKomunikatu";
import PasekKomunikatuWidok from "./PasekKomunikatuWidok";
import styles from "./AnnouncementTopBar.module.css";

/* ⚠️ [obszar 5, 2026-09-17] Lokalny `toPlainText` STAD ZNIKNAL i nie zostal przeniesiony — robil
   o jedno mniej, niz trzeba. Sklejal CALA tresc w jeden wiersz (`\s+` -> spacja), wiec o „dwoch
   pierwszych linijkach" nie moglo byc mowy: granice akapitow przestawaly istniec, zanim cokolwiek
   dalo sie policzyc. Podzial na linijki liczy `utils/tekstKomunikatu` (tam tez, DLACZEGO nie jest
   to `slice` ani `-webkit-line-clamp`). */

/** Ile pasków stoi naraz nad nagłówkiem na komputerze. Na telefonie CSS zostawia jeden (patrz arkusz). */
export const LIMIT_WIDOCZNYCH_PASKOW = 2;

/** Zdarzenie okna: serwer dał znać, że paski się zmieniły (hub „PasekKomunikatuZmiana” → NotificationContext). */
export const ZDARZENIE_ZMIANY_PASKA = "pasek-komunikatu-zmiana";

// Siatka bezpieczeństwa dla karty bez połączenia na żywo — te same odstępy co baner trybu testowego.
const ODSTEP_PO_POWROCIE_MS = 60 * 1000;
const ODSTEP_ODPYTANIA_MS = 5 * 60 * 1000;
// setTimeout nie przyjmuje opóźnień dłuższych niż ok. 24,8 dnia — długie okresy dzielimy na godzinne kroki.
const NAJDLUZSZY_KROK_ZEGARA_MS = 60 * 60 * 1000;

/** Chwila końca okresu wyświetlania (ms) albo null, gdy pasek nie ma daty końca. */
const koniecMs = (b) => {
    if (!b?.topBarTo) return null;
    const t = new Date(b.topBarTo).getTime();
    return Number.isNaN(t) ? null : t;
};
/** Koniec okresu jest chwilą WYŁĄCZNĄ — tak samo liczy serwer (pasek „do 15:00” znika o 15:00). */
const trwa = (b, teraz) => {
    const k = koniecMs(b);
    return k === null || k > teraz;
};

/**
 * [BANER] Paski komunikatów nad nagłówkiem aplikacji.
 *
 * Zrodlo: GET /api/announcements/topbar — komunikaty typu „pasek" z /admin/announcements, w okresie
 * wyświetlania od–do, adresowane do zalogowanego konta (adresatów liczy serwer, na żywo, z reguły widoczności).
 * Zamkniecie (✕) oznacza komunikat jako przeczytany po stronie serwera; pasek, którego autor nie pozwolił
 * zamykać, nie ma ✕.
 *
 * UWAGA: pasek NIE JEST juz czarny. Byl — i to bylo usterka: siedem zaszytych kolorow bez
 * jednego tokenu sprawialo, ze przy kazdej z 22 palet i w obu motywach wygladal identycznie,
 * jak element z innej aplikacji. Dzis kolor bierze z tokenu akcentu, a wariant komunikatu
 * podmienia go na semantyczny (ostrzezenie / pilne) — patrz AnnouncementTopBar.module.css.
 *
 * ── [polecenie wlasciciela 2026-09-17, obszar 5] CO POKAZUJE PASEK ────────────────────────────
 * „W pasku wyswietlaj tytul, kategorie oraz 2 pierwsze linijki z opcja klikniecia do otwarcia
 * w oknie modal." Wygląd jednego paska rysuje `PasekKomunikatuWidok` — ten sam komponent, co podgląd
 * w oknie edycji.
 *
 * ── [polecenie właściciela 2026-10-04] KILKA PASKÓW, OKRES, NA ŻYWO ───────────────────────────
 * • KILKA NARAZ. Było: jeden pasek z licznikiem „1 z N” — żeby zobaczyć drugi, trzeba było pierwszy
 *   zamknąć, czyli go stracić. Jest: na komputerze dwa paski jeden pod drugim, reszta pod „+N”;
 *   na telefonie jeden zwarty pasek i „+N” na wszystkie pozostałe (nagłówek nie może zjechać w dół ekranu).
 *   Limit robi CSS (media query), nie nasłuch rozmiaru okna. Kolejność ustala serwer:
 *   Pilne → Ostrzeżenie → Informacja, potem najnowszy start.
 * • KONIEC OKRESU. Pasek zna swoją datę „do” i znika o tej chwili sam, zegarem przeglądarki, bez pytania serwera.
 * • POCZĄTEK OKRESU, EDYCJA, ZAKOŃCZENIE, USUNIĘCIE. Serwer wysyła sygnał (bez treści), po którym pasek pyta
 *   serwer ponownie. Karta bez połączenia na żywo odświeża się po powrocie do niej i co 5 minut.
 *
 * ⚠️ [2026-09-17] Zamknięcie na jednym urządzeniu chowa pasek na pozostałych otwartych urządzeniach konta:
 * serwer wysyła `AnnouncementRead`, `Layout` zamienia to na zdarzenie okna `announcement-read-elsewhere`.
 */
const AnnouncementTopBar = () => {
    const [bars, setBars] = useState([]);
    const [closing, setClosing] = useState(null);
    const [rozwiniete, setRozwiniete] = useState(false);
    // Licznik podbijany zegarem końca okresu — sam w sobie nic nie znaczy, wymusza ponowne przeliczenie listy.
    const [takt, setTakt] = useState(0);
    /* Komunikat otwarty w oknie pelnej tresci. Trzymamy CALY obiekt, nie identyfikator: okno ma juz
       wszystko, co pokazuje (tytul, kategoria, autor, data, tresc, obrazek), wiec drugie zapytanie
       do serwera byloby zadaniem o dane, ktore leza w pamieci. */
    const [otwarty, setOtwarty] = useState(null);
    const ostatnio = useRef(0);

    const load = useCallback(async () => {
        ostatnio.current = Date.now();
        try {
            const res = await apiService.getTopBarAnnouncements();
            setBars(Array.isArray(res.data) ? res.data : []);
        } catch {
            /* brak paskow / brak sesji — pasek po prostu sie nie renderuje */
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const h = () => load();
        const poPowrocie = () => {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - ostatnio.current < ODSTEP_PO_POWROCIE_MS) return;
            load();
        };
        // Nowy komunikat nadany w trakcie sesji (ten sam kanal co modal komunikatow).
        window.addEventListener("announcement-received", h);
        // Komunikat ZAMKNIETY na innym urzadzeniu tego konta.
        window.addEventListener("announcement-read-elsewhere", h);
        // Pasek dodany, zmieniony, zakończony, usunięty albo jego czas „od” właśnie nadszedł.
        window.addEventListener(ZDARZENIE_ZMIANY_PASKA, h);
        window.addEventListener("realtime-reconnected", h);
        document.addEventListener("visibilitychange", poPowrocie);
        const zegar = setInterval(() => {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - ostatnio.current < ODSTEP_ODPYTANIA_MS) return;
            load();
        }, ODSTEP_ODPYTANIA_MS);
        return () => {
            clearInterval(zegar);
            window.removeEventListener("announcement-received", h);
            window.removeEventListener("announcement-read-elsewhere", h);
            window.removeEventListener(ZDARZENIE_ZMIANY_PASKA, h);
            window.removeEventListener("realtime-reconnected", h);
            document.removeEventListener("visibilitychange", poPowrocie);
        };
    }, [load]);

    const teraz = Date.now();
    const aktywne = bars.filter((b) => trwa(b, teraz));

    /* ZEGAR KOŃCA OKRESU: jeden budzik na najbliższą datę „do” wśród pasków, które jeszcze trwają.
       Po przebudzeniu lista liczy się od nowa (pasek po terminie odpada) i budzik ustawia się na następną datę. */
    const najblizszyKoniec = aktywne.reduce((min, b) => {
        const k = koniecMs(b);
        return k !== null && (min === null || k < min) ? k : min;
    }, null);
    useEffect(() => {
        if (najblizszyKoniec === null) return undefined;
        const zaIle = Math.max(0, najblizszyKoniec - Date.now());
        const budzik = setTimeout(() => setTakt((n) => n + 1), Math.min(zaIle, NAJDLUZSZY_KROK_ZEGARA_MS));
        return () => clearTimeout(budzik);
    }, [najblizszyKoniec, takt]);

    /* ⚠️ Okno zostaje otwarte nawet wtedy, gdy pasek zniknal (zamkniecie na innym urzadzeniu,
       koniec okna czasowego, odswiezenie listy). Zamkniecie komus okna z treścia, ktora wlasnie
       czyta, byloby gorsze niz pokazanie jej chwile dluzej — a zapisu to okno nie robi zadnego. */
    const oknoPelne = otwarty ? (
        <OknoPelnegoKomunikatu komunikat={otwarty} onClose={() => setOtwarty(null)} />
    ) : null;

    if (aktywne.length === 0) return oknoPelne;

    const dismiss = async (id) => {
        if (closing !== null) return;
        setClosing(id);
        try {
            await apiService.markAnnouncementRead(id);
        } catch {
            /* nawet gdy zapis padnie, chowamy lokalnie — wroci przy nastepnym wejsciu */
        } finally {
            setBars((prev) => prev.filter((b) => b.id !== id));
            setClosing(null);
        }
    };

    const pierwszy = aktywne[0];
    const pozostale = aktywne.slice(1);
    // Na komputerze drugi pasek stoi na stałe, więc „reszta” to wszystko od trzeciego; na telefonie — od drugiego.
    const resztaKomputer = Math.max(0, aktywne.length - LIMIT_WIDOCZNYCH_PASKOW);
    const resztaTelefon = aktywne.length - 1;
    const jestCoRozwijac = pozostale.length > 0;
    const pokazRozwiniete = rozwiniete && jestCoRozwijac;

    const przyciskReszty = (ile, klasa) => (ile > 0 && !pokazRozwiniete ? (
        <button
            type="button"
            className={`${styles.wiecej} ${klasa}`}
            onClick={() => setRozwiniete(true)}
            data-pasek-reszta={ile}
            aria-label={`Pokaż pozostałe komunikaty: ${ile}`}
        >
            +{ile}
        </button>
    ) : null);

    const widok = (b, pozycja, dodatek = null) => (
        <PasekKomunikatuWidok
            key={b.id}
            komunikat={b}
            pozycja={pozycja}
            onOtworz={() => setOtwarty(b)}
            onZamknij={() => dismiss(b.id)}
            zamykanie={closing !== null}
            dodatek={dodatek}
        />
    );

    return (
        <>
            <div className={styles.stos} data-paski-komunikatow={aktywne.length}>
                {widok(pierwszy, 1, przyciskReszty(resztaTelefon, styles.wiecejTelefon))}
                {jestCoRozwijac && (
                    <div
                        className={pokazRozwiniete ? `${styles.pozostale} ${styles.pozostaleRozwiniete}` : styles.pozostale}
                        data-paski-pozostale={pokazRozwiniete ? "rozwiniete" : "zwiniete"}
                    >
                        {pozostale.map((b, i) => widok(
                            b,
                            i + 2,
                            i === 0 ? przyciskReszty(resztaKomputer, styles.wiecejKomputer) : null,
                        ))}
                        {pokazRozwiniete && (
                            <div className={styles.zwinRzad}>
                                <button type="button" className={styles.zwin} onClick={() => setRozwiniete(false)}>
                                    Zwiń
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {oknoPelne}
        </>
    );
};

export default AnnouncementTopBar;
